import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { DialogDefaultV2 } from '../../../components/v2';
import {
  SECAO_RELATORIO_FINANCEIRO,
  SECAO_RELATORIO_FINANCEIRO_OPTIONS,
  buildExportFilterPreview,
  getDefaultExportSecoes,
  isExportSecaoDisabled,
} from './constants';

const ExportPdfDialog = ({
  open,
  onClose,
  onExport,
  loading = false,
  filter,
  activeCategoria = null,
  usuarioNome = null,
  segmentLabel = null,
  periodInvalid = false,
}) => {
  const [applyFilters, setApplyFilters] = useState(false);
  const [selectedSecoes, setSelectedSecoes] = useState([]);

  useEffect(() => {
    if (!open) return;
    setApplyFilters(false);
    setSelectedSecoes(getDefaultExportSecoes(false, filter));
  }, [open, filter]);

  const previewChips = useMemo(
    () => buildExportFilterPreview({
      filter,
      activeCategoria,
      applyFilters,
      usuarioNome,
      segmentLabel,
    }),
    [filter, activeCategoria, applyFilters, usuarioNome, segmentLabel],
  );

  const hasDetalhado = selectedSecoes.includes(SECAO_RELATORIO_FINANCEIRO.DETALHADO);
  const canExport = selectedSecoes.length > 0
    && (!applyFilters || !periodInvalid)
    && !loading;

  const handleToggleSecao = (value) => {
    setSelectedSecoes((prev) => (
      prev.includes(value)
        ? prev.filter((s) => s !== value)
        : [...prev, value]
    ));
  };

  const handleApplyFiltersChange = (event) => {
    const nextApply = event.target.value === 'current';
    setApplyFilters(nextApply);
    setSelectedSecoes((prev) => {
      const filtered = prev.filter(
        (s) => !isExportSecaoDisabled(s, nextApply, filter),
      );
      if (filtered.length > 0) return filtered;
      return getDefaultExportSecoes(nextApply, filter);
    });
  };

  const handleConfirm = () => {
    if (!canExport) return;
    onExport({ secoes: selectedSecoes, applyFilters });
  };

  return (
    <DialogDefaultV2
      open={open}
      handlerCancel={onClose}
      handlerConfirm={handleConfirm}
      title="Exportar PDF"
      confirmMessage={loading ? 'Gerando PDF…' : 'Exportar PDF'}
      cancelMessage="Cancelar"
      maxWidth="sm"
      fullWidth
      disabledConfirmButton={!canExport}
    >
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Escopo do relatório
          </Typography>
          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={applyFilters ? 'current' : 'default'}
              onChange={handleApplyFiltersChange}
            >
              <FormControlLabel
                value="default"
                control={<Radio size="small" />}
                label="Usar filtro padrão"
              />
              <FormControlLabel
                value="current"
                control={<Radio size="small" />}
                label="Usar filtros atuais da tela"
              />
            </RadioGroup>
          </FormControl>
        </Box>

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            O PDF será gerado com:
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {previewChips.map((chip) => (
              <Chip key={chip} label={chip} size="small" variant="outlined" />
            ))}
          </Stack>
          {applyFilters && periodInvalid && (
            <FormHelperText error sx={{ mt: 1 }}>
              Período inválido — ajuste as datas nos filtros ou escolha usar filtro padrão.
            </FormHelperText>
          )}
        </Box>

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Seções do PDF
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Escolha o que incluir. Cada item corresponde a um bloco do relatório.
          </Typography>
          <Stack spacing={0.5}>
            {SECAO_RELATORIO_FINANCEIRO_OPTIONS.map((option) => {
              const disabled = isExportSecaoDisabled(option.value, applyFilters, filter);
              const row = (
                <FormControlLabel
                  key={option.value}
                  control={(
                    <Checkbox
                      checked={selectedSecoes.includes(option.value)}
                      disabled={disabled || loading}
                      onChange={() => handleToggleSecao(option.value)}
                      size="small"
                      sx={{ alignSelf: 'flex-start', pt: 0.5 }}
                    />
                  )}
                  label={(
                    <Box sx={{ py: 0.25 }}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color={disabled ? 'text.disabled' : 'text.primary'}
                      >
                        {option.label}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        {option.description}
                      </Typography>
                    </Box>
                  )}
                  sx={{ alignItems: 'flex-start', ml: 0, mr: 0 }}
                />
              );

              if (!disabled) return row;

              return (
                <Tooltip
                  key={option.value}
                  title="Indisponível ao filtrar por um usuário específico"
                  placement="right"
                >
                  <Box>{row}</Box>
                </Tooltip>
              );
            })}
          </Stack>
          {selectedSecoes.length === 0 && (
            <FormHelperText error sx={{ mt: 0.5 }}>
              Selecione ao menos uma seção.
            </FormHelperText>
          )}
        </Box>

        {hasDetalhado && (
          <Alert severity="warning" variant="outlined" sx={{ borderRadius: 1 }}>
            Períodos longos com extrato detalhado podem gerar PDF grande.
          </Alert>
        )}

        <Alert severity="info" variant="outlined" icon={false} sx={{ borderRadius: 1, py: 0.75 }}>
          <Typography variant="caption" color="text.secondary">
            Gráficos da tela não entram no PDF — apenas dados tabulares e indicadores.
          </Typography>
        </Alert>
      </Stack>
    </DialogDefaultV2>
  );
};

ExportPdfDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  filter: PropTypes.object,
  activeCategoria: PropTypes.string,
  usuarioNome: PropTypes.string,
  segmentLabel: PropTypes.string,
  periodInvalid: PropTypes.bool,
};

ExportPdfDialog.defaultProps = {
  loading: false,
  filter: null,
  activeCategoria: null,
  usuarioNome: null,
  segmentLabel: null,
  periodInvalid: false,
};

export default ExportPdfDialog;
